import { useState, useEffect, type ReactNode } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePush } from "@/hooks/use-push";

const AUTO_DISMISS_MS = 10_000;

export function PushPrompt(): ReactNode {
  const { permission, requestPermission } = usePush();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  // Delay mount slightly so it animates in after page load
  useEffect(() => {
    if (permission !== "default" || dismissed) return;
    const t = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(t);
  }, [permission, dismissed]);

  // Auto-dismiss after 10s
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [visible]);

  if (permission !== "default" || dismissed) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={[
        "fixed bottom-4 left-4 z-50 w-80 max-w-[calc(100vw-2rem)]",
        "rounded-xl border border-border/60 bg-card shadow-lg",
        "dark:ring-1 dark:ring-white/10",
        "transition-all duration-300 ease-out",
        visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      ].join(" ")}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Bell className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 pt-0.5">
          <p className="text-sm font-medium leading-snug">
            {"רוצה לקבל התראות על הזמנות חדשות?"}
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => {
                void requestPermission();
                setDismissed(true);
              }}
            >
              {"כן, הפעל"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => setDismissed(true)}
            >
              {"אולי אחר כך"}
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="סגור"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
